import {
  getStorage,
  connectStorageEmulator,
  ref,
  deleteObject,
  getDownloadURL,
  uploadBytes,
  uploadBytesResumable,
} from "firebase/storage";
import { app } from "./app";

export { ref, deleteObject, getDownloadURL, uploadBytes, uploadBytesResumable };
export const storage = getStorage(
  app,
  import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
);

/**
 * How long a failing **upload** is allowed to keep trying (Jira NAV-139).
 *
 * The SDK's default is **ten minutes**, and it is a total budget covering the
 * retries and the attempt that succeeds — so a bucket the browser cannot reach
 * at all reports nothing for ten minutes. That is where "many minutes later a
 * warning arrives saying the document could not be attached" came from, and no
 * message written at the call site improves on an answer that lands after the
 * person has left the page.
 *
 * Two minutes is chosen against the ceiling this app actually allows:
 * `ATTACHMENT_MAX_BYTES` is 25 MB, which still fits inside the window on a link
 * of roughly 1.7 Mbit/s, so a genuinely slow upload of the largest permitted
 * file is not cut short.
 *
 * **`maxOperationRetryTime` is deliberately left at the SDK's two minutes.**
 * This is one app-wide instance, so lowering it would shorten the budget for
 * every download URL, avatar read, object delete and metadata read in the app —
 * a change to surfaces nobody asked about, justified by a complaint about
 * attaching. The transfer that hung is the upload, and that is the one bounded
 * here.
 */
storage.maxUploadRetryTime = 2 * 60 * 1000;

if (import.meta.env.VITE_USE_FIREBASE_EMULATOR === "true") {
  connectStorageEmulator(
    storage,
    import.meta.env.VITE_FIREBASE_EMULATOR_HOST,
    // Default so a mode that sets VITE_USE_FIREBASE_EMULATOR without the
    // port vars connects to the standard port instead of NaN, which fails
    // silently.
    parseInt(import.meta.env.VITE_FIREBASE_EMULATOR_STORAGE_PORT, 10) || 9199,
  );
}
